import { Box, Divider, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import Chip from '/@/components/bayer/chip';
import {
    BASE_STEPS,
    getLabelStep,
    mergeStepsAndStages,
} from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS';
import { _ } from '/@/locale';
import type { CropMonitoring, CropMonitoringApplicationReports } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';

const useCMStages = (stages, plantingDate, applicationResults = []) => {
    const steps = mergeStepsAndStages(
        BASE_STEPS.filter((s) => !s.empty),
        stages,
        plantingDate
    )
        .filter((s) => s.date)
        .map((step, i, array) => {
            const nextStep = array[i + 1];
            const results = applicationResults.filter((r) => {
                const eventStartDate = dayjs(r.eventStartDate);
                if (!nextStep) {
                    return eventStartDate.isSame(dayjs(step.date)) || eventStartDate.isAfter(dayjs(step.date));
                }
                return eventStartDate.isBetween(dayjs(step.date), dayjs(nextStep?.date), null, '[)');
            });
            return { ...step, results };
        });
    return steps;
};

const CropMonitoringReportApplications = ({
    cropMonitoring,
    applicationReportsQuery,
    isPdf,
}: {
    cropMonitoring: CropMonitoring;
    applicationReportsQuery: CropMonitoringApplicationReports;
    isPdf: boolean;
}) => {
    const cropData = getCropMonitoringData(cropMonitoring);
    const applicationResults = useMemo(
        () =>
            applicationReportsQuery.data?.results
                .flatMap((report) => report.results)
                .filter((r) => {
                    const eventStartDate = dayjs(r.eventStartDate);
                    return eventStartDate.isBetween(
                        dayjs(cropMonitoring?.plantingDate),
                        dayjs(cropMonitoring?.result?.harvestDate),
                        null,
                        '[]'
                    );
                }),
        [applicationReportsQuery.data]
    );

    const steps = useCMStages(cropData?.stages || [], cropMonitoring?.plantingDate, applicationResults);
    const [filter, setFilter] = useState<string | null>(null);
    const filters = useMemo<Set<string>>(
        () => new Set((applicationResults || []).map((result) => result.applicationName)),
        [applicationResults]
    );
    const filteredSteps = useMemo(
        () =>
            steps
                .map((step) => ({
                    ...step,
                    results: step.results.filter((result) => (filter ? result.applicationName === filter : true)),
                }))
                .filter((step) => {
                    const hasResult = step.results.length > 0;
                    const vegetative = step.vegetativeStage === undefined || step.vegetativeStage <= 5;
                    return hasResult || vegetative;
                }),
        [steps, filter]
    );
    useEffect(() => {
        if (filteredSteps)
            document
                .querySelector('#application')
                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, [filteredSteps]);

    if ((applicationResults || []).length === 0) return null;

    return (
        <Stack
            sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                height: '100%',
            }}
        >
            <Stack p={2} mb='auto'>
                <Typography variant='h6' color='primary'>
                    {_('Aplicaciones')}
                </Typography>

                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Detalle de las aplicaciones de productos de protección y fertilizantes.')}
                </Typography>
            </Stack>
            <Divider />
            <Stack width='100%' spacing={3} p={2}>
                {!isPdf && (
                    <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                        <Chip label={_('Todas')} onClick={() => setFilter(null)} selected={filter === null} />
                        {[...filters].map((key) => (
                            <Chip key={key} label={key} onClick={() => setFilter(key)} selected={filter === key} />
                        ))}
                    </Stack>
                )}
                <Stack spacing={1} p={1} pt={0} direction='row' overflow='auto' justifyContent='space-between'>
                    {filteredSteps?.map((step) => (
                        <Stack
                            key={step.id}
                            spacing={2}
                            alignItems='center'
                            position='relative'
                            paddingTop={`${(step.results.length - 1) * 80}px`}
                            mt={'auto !important'}
                        >
                            <Stack spacing={1} alignItems='center' position='absolute' top={0}>
                                {step.results.map((result) => (
                                    <Box key={result.id} id={'application'}>
                                        <Stack
                                            alignItems='center'
                                            sx={{
                                                background: 'white',
                                                border: (t) => `1px solid ${t.palette.divider}`,
                                                width: 'max-content',
                                                boxShadow: 'var(--box-shadow-1)',
                                            }}
                                            p={1}
                                        >
                                            <Typography variant='caption' color='primary.mui.inactive'>
                                                {result.chemicalProductName}
                                            </Typography>
                                            <Typography variant='caption' color='primary.mui.inactive'>
                                                {result.applicationName}
                                            </Typography>
                                            <Typography
                                                variant='caption'
                                                color='primary'
                                            >{`${round(result.averageApplicationRate, 2)} ${result.applicationRateUnits}`}</Typography>
                                            <Typography
                                                variant='caption'
                                                color='primary'
                                            >{`(${result.eventStartDate})`}</Typography>
                                        </Stack>
                                        <Box
                                            sx={{
                                                background: 'primary',
                                                justifySelf: 'center',
                                                width: '12px',
                                                height: '12px',
                                                boxShadow: '0px 0px 5px -2px',
                                                border: '2px solid white',
                                                borderRadius: '100%',
                                                mt: 0.5,
                                                visibility: cropMonitoring?.crop !== 'corn' ? 'hidden' : 'visible',
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                            <img
                                src={step?.image}
                                alt={step.displayName}
                                height={cropMonitoring?.crop !== 'corn' ? 50 : isPdf ? 200 : 236}
                                style={{ visibility: cropMonitoring?.crop !== 'corn' ? 'hidden' : 'visible' }}
                            />
                            <Stack alignItems='center'>
                                <Typography
                                    variant='body2'
                                    color='primary.mui.inactive'
                                    sx={{ visibility: cropMonitoring?.crop !== 'corn' ? 'hidden' : 'visible' }}
                                >
                                    {getLabelStep(step?.vegetativeStage, step?.phenology)}
                                </Typography>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {formatDate(new Date(step.date), { dayMonthOnly: true })}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default CropMonitoringReportApplications;
